// Site-wide settings — one document, read by every signed-in user.
//
// This is for switches and standing details that change what the *public site*
// offers, as opposed to content (which is the experience / collection / guide
// records) or deployment configuration (which is `functions/.env.<projectId>`
// and cannot be changed without a redeploy). A feature flag belongs here
// precisely because the point of one is to be flipped by a person at the moment
// they decide, not by an engineer at the moment they deploy; the emergency
// number belongs here for the same reason, one desk phone number down.
//
// Deliberately one document rather than one per flag: the whole thing is read on
// every page load by every user, so it must stay a single cheap subscription.

export const coll_settings = 'settings';

/** The document this file's first half describes. */
export const doc_siteSettings = 'site';

/**
 * The second document, and the reason `settings/{id}` is **not** readable by
 * every signed-in user — see firestore.rules. It holds our bank account.
 */
export const doc_invoicingSettings = 'invoicing';

/**
 * The third: the partner agreement, in its own document because it is the one
 * thing here read by people who are not signed in yet — see firestore.rules and
 * `TermsSettingsType`.
 */
export const doc_termsSettings = 'terms';

export interface SiteSettingsType {
  /**
   * The number a guest rings on the day when something has gone wrong — they
   * cannot find the venue, they are running late, the door is locked.
   *
   * Printed on the guest recap attached to every booking confirmation, which is
   * the one piece of paper the guest is actually holding when that happens. It
   * is therefore a *staffed* number rather than the office switchboard: whoever
   * answers it has to be able to help somebody standing in the wrong lane.
   *
   * Free text rather than a parsed number, because it is printed and dialled,
   * never matched on — `+81 90 1234 5678` and `090-1234-5678` are both answers
   * and neither is ours to reformat. Blank is a legitimate state and means the
   * recap simply does not raise the subject; see `emergency` in
   * util_bookingRecapPdf.ts.
   */
  emergencyPhone: string;

  /**
   * Whether partners can see the Tour Leaders page at all — the nav links
   * in the header and footer, and the `/tour-leaders` route itself.
   *
   * Both, not just the links: a hidden section reachable by anyone who kept the
   * URL is not hidden, and a concierge who bookmarked it last week is exactly
   * the person a feature flag is meant to catch. Eighty Days admins are the
   * exception and keep reaching it either way, which is what makes the flag
   * usable — you can look at the thing before you turn it on.
   */
  tourLeadersVisible: boolean;

  updatedAt?: string;
  updatedByUid?: string;
}

/**
 * What the site does before anybody has saved a setting — and, since the
 * document does not exist until the first save, what it does today.
 *
 * Tour leaders default to *visible* so that introducing this switch changes
 * nothing on its own. Turning the section off is then a deliberate act with a
 * click behind it, rather than something that happened silently at deploy time
 * and has to be discovered.
 */
export function makeDefaultSiteSettings(): SiteSettingsType {
  return {
    // Nothing printed until somebody has said what the number is. A placeholder
    // here would go out on a guest's confirmation and be rung.
    emergencyPhone: '',
    tourLeadersVisible: true,
  };
}

/** Stored settings, with anything added to the type since filled in. */
export function normaliseSiteSettings(data: Partial<SiteSettingsType> | undefined): SiteSettingsType {
  return { ...makeDefaultSiteSettings(), ...data };
}

// --- Invoicing ---
//
// Everything the monthly invoice prints that is not derived from the bookings:
// who we are, where to pay us, and the two bits of standing wording at the
// bottom. All of it appears on a document sent to a partner, and all of it changes
// by a person deciding rather than by a deploy, which is the same argument that
// put the flags above in Firestore.
//
// A **second document**, deliberately. `settings/site` is read by every signed-in
// user on every page load by design, and this holds a bank account number. The
// two are separated in firestore.rules by exactly that: `settings/site` is
// readable when signed in, everything else in the collection is admin-only.

export interface InvoicingSettingsType {
  // The issuer block, printed top-right. Japanese, as the template is.
  companyName: string;
  /** 〒170-0013 */
  postalCode: string;
  /** Two lines on the invoice; a newline separates them. */
  address: string;
  phone: string;
  /**
   * 登録番号 — the qualified-invoice registration number, `T` + 13 digits.
   * Without it the document is not a 適格請求書 and the partner cannot claim the
   * input credit, so it is printed whenever it is set and simply omitted when
   * it is not, rather than printed blank.
   */
  registrationNumber: string;

  // 口座 — where the partner pays. Four lines, as the template has them.
  bankName: string;
  bankBranch: string;
  /** 普通 / 当座. Printed as `口座番号（普通）12345678`. */
  accountType: string;
  accountNumber: string;
  /** The holder in katakana, as banks require it. */
  accountHolder: string;

  /**
   * Where the company seal (角印) lives in Cloud Storage, e.g.
   * `internal/hanko.png`. Blank means invoices are rendered unsealed.
   *
   * A path rather than the image, because the image must not be in Firestore:
   * `settings/invoicing` is admin-readable, and a seal readable by any signed-in
   * admin session is a seal that leaks with the first stolen session. It lives
   * under `internal/` in Storage, which `storage.rules` closes to every client —
   * only the Cloud Function reaches it, through the Admin SDK.
   *
   * Set this only once the file is actually uploaded. A configured seal that
   * cannot be read fails the render deliberately, rather than quietly sending a
   * month of unsealed invoices — see `renderInvoicePdf`.
   */
  hankoPath: string;

  /** The 税率 every line is stated at unless something says otherwise. */
  defaultTaxRate: number;

  /** 備考 — the standing note at the foot of every invoice. */
  remarks: string;

  updatedAt?: string;
  updatedByUid?: string;
}

/**
 * What the invoice prints before anybody has filled this in.
 *
 * Eighty Days' own details, taken from the 請求書 template these were built
 * from, because they are not a placeholder — they are the answer, and an invoice
 * is not a document to ship with `YOUR COMPANY HERE` on it waiting to be
 * noticed. The bank block included: it is where partners already pay, and it is
 * printed on every invoice we have ever sent.
 *
 * `registrationNumber` is the deliberate exception, and the only one. It is the
 * field where a stale value would be worse than a missing one — a partner's
 * accountant relies on it to claim the input tax credit, and a wrong number
 * fails silently at their end months later. Blank forces somebody to enter the
 * live one once, and `invoicingRequiredFields` refuses to issue until they have.
 *
 * These are only defaults. The stored `settings/invoicing` document overrides
 * every one of them, and changing an account never needs a release.
 */
export function makeDefaultInvoicingSettings(): InvoicingSettingsType {
  return {
    companyName: 'Eighty Days株式会社',
    postalCode: '〒170-0013',
    address: '東京都豊島区東池袋２丁目６０−３\nグレイスロータリービル8F',
    phone: '+81 3 6730 8485',
    registrationNumber: '',
    bankName: '三井住友銀行(0009)',
    bankBranch: '五反田支店(653)',
    accountType: '普通',
    accountNumber: '8608044',
    accountHolder: 'エイテイデイズ（カ',
    // Off until somebody has uploaded a seal and said where it is. Defaulting to
    // a path would fail every render on a project that has no such file.
    hankoPath: '',
    defaultTaxRate: 10,
    remarks: 'お振込み手数料はご負担をお願いいたします',
  };
}

/** Stored invoicing settings, with anything added to the type since filled in. */
export function normaliseInvoicingSettings(
  data: Partial<InvoicingSettingsType> | undefined,
): InvoicingSettingsType {
  return { ...makeDefaultInvoicingSettings(), ...data };
}

/**
 * Which settings must be filled in before an invoice can be raised.
 *
 * Checked at issue rather than at generation: a draft is worth looking at even
 * from a half-configured system, but a document that goes to a partner without a
 * bank account on it is a document nobody can pay.
 */
export const invoicingRequiredFields = [
  'companyName',
  'address',
  'registrationNumber',
  'bankName',
  'bankBranch',
  'accountNumber',
  'accountHolder',
] as const satisfies readonly (keyof InvoicingSettingsType)[];

export function missingInvoicingSettings(
  settings: InvoicingSettingsType,
): (keyof InvoicingSettingsType)[] {
  return invoicingRequiredFields.filter((field) => !String(settings[field] ?? '').trim());
}

// --- Terms ---
//
// The agreement a partner's manager accepts during onboarding. It used to be
// prose transcribed into a component (`PartnerTermsText`); it is a document
// because the wording is the business's to write and revise, and a legal
// agreement is the last thing that should need an engineer and a deploy to
// correct.
//
// A **third document**, and not part of `settings/site`, for two reasons. It is
// long — several kilobytes of prose — and `settings/site` is fetched on every
// page load by every user, which is exactly what it must stay cheap enough to
// do. And it is world-readable, which `settings/site` is not: the registration
// wizard puts the agreement in front of a manager at step 2, before their
// account exists at step 3, so the person it binds necessarily reads it as a
// stranger. See firestore.rules.
//
// This document is the *only* copy of the wording. There is deliberately no
// compiled-in fallback: a second copy in the bundle is a second version of a
// legal agreement, and the question "which one did they accept?" has to have one
// answer. Before it is filled in the step shows that there is nothing to show —
// see `termsSettingsEmpty` — rather than quietly presenting older wording.
//
// What is not here yet: a version, and any prompt to re-accept.
// `PARTNER_TERMS_VERSION` is still a constant in `types_partner.ts`, so editing
// the wording does not ask anybody who has already accepted to look again.

export interface TermsSettingsType {
  /** Heading above the first block. */
  ndaHeading: string;
  /** The NDA itself. Rich text (HTML), as written in the admin's `RichTextField`. */
  ndaBody: string;

  /** Heading above the second block, which the step rules off from the first. */
  serviceHeading: string;
  /** The general terms of service. Rich text, as above. */
  serviceBody: string;

  updatedAt?: string;
  updatedByUid?: string;
}

/**
 * Blank terms — what the site holds until somebody has written them, and what
 * every field falls back to individually.
 *
 * Empty rather than seeded with the wording partners have accepted so far: see
 * the note above. The admin card says so plainly when it finds this state.
 */
export function makeEmptyTermsSettings(): TermsSettingsType {
  return {
    ndaHeading: '',
    ndaBody: '',
    serviceHeading: '',
    serviceBody: '',
  };
}

/** Stored terms, with anything added to the type since filled in. */
export function normaliseTermsSettings(data: Partial<TermsSettingsType> | undefined): TermsSettingsType {
  return { ...makeEmptyTermsSettings(), ...data };
}

/**
 * Whether there is any agreement to show at all.
 *
 * The bodies alone: a heading with nothing under it is not an agreement, and
 * both blocks are optional individually — one long NDA and no separate terms of
 * service is a perfectly good answer.
 */
export function termsSettingsEmpty(settings: TermsSettingsType): boolean {
  return settings.ndaBody.trim() === '' && settings.serviceBody.trim() === '';
}
